import React, { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStacklineTheme } from '@stackline/ui';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMainEntityType, useTimePeriods, useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { useMainTimePeriod } from 'src/utils/Hooks/typedHooks';
import { useBeaconFilters } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBeaconFilters';
import { BarChartData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import { EntityCategoryDatum } from 'src/components/BeaconV2Components/TopEntityChartV2/types';
import { FIVE_MIN, THIRTY_MIN } from 'src/utils/constants';
import ReactDOMServer from 'react-dom/server';
import { generateProductLink } from 'src/utils/searchLinks';

interface VariationsPageSizeCardsProps {
  title: string;
  queryFn: () => Promise<BarChartData>;
  renderSubtitle?: () => string | React.ReactNode;
}

const SINGLE_BAR_CHART_THRESHOLD = 1000;

export const VariationsPageSizeCards = (props: VariationsPageSizeCardsProps) => {
  const { title, queryFn, renderSubtitle } = props;
  const { mainTimePeriod } = useTimePeriods();
  const { displayName: mainTimePeriodDisplayName } = useMainTimePeriod();
  const { appFilters } = useBeaconFilters();
  const entityType = useMainEntityType();
  const retailerId = useRetailerId();
  const saveImageRef = useRef<HTMLDivElement>(null);
  const theme = useStacklineTheme();

  const filtersKey = useMemo(() => {
    return appFilters ? JSON.stringify(appFilters) : '';
  }, [appFilters]);

  const { data: chartSeries, isLoading } = useQuery({
    queryKey: [
      'variations-page-size',
      entityType,
      mainTimePeriod.startWeek,
      mainTimePeriod.endWeek,
      retailerId,
      filtersKey
    ],
    queryFn: async () => {
      const variationPageSizeQueryData = await queryFn();

      if (variationPageSizeQueryData?.chartData) {
        const originalSeries = variationPageSizeQueryData.chartData[0];
        const limitedData = originalSeries.data.slice(0, SINGLE_BAR_CHART_THRESHOLD);

        const categories = limitedData.map((item, idx) => {
          const parentSku = item[0];

          return {
            id: parentSku,
            name: parentSku,
            type: 'product',
            retailerSku: parentSku,
            stacklineSku: parentSku
          };
        });

        return [
          {
            type: 'column' as const,
            data: limitedData,
            metricType: originalSeries.metricType || METRICTYPE.VOLUME,
            categories
          }
        ];
      }

      return [];
    },
    staleTime: FIVE_MIN,
    gcTime: THIRTY_MIN
  });

  const getSubtitle = () => {
    if (!renderSubtitle) return null;

    const subtitleContent = renderSubtitle();
    if (typeof subtitleContent === 'string') {
      return (
        <BeaconSubtitleContainer>
          <Text variant="body1">{subtitleContent}</Text>
        </BeaconSubtitleContainer>
      );
    }

    return <BeaconSubtitleContainer>{subtitleContent}</BeaconSubtitleContainer>;
  };

  const xAxisFormatter = (categoryData: EntityCategoryDatum, id?: string, tooltipId?: string) => {
    const { name } = categoryData;

    if (name) {
      const productLink = generateProductLink(retailerId, name);

      const linkElement = (
        <div id={id} style={{ position: 'relative' }}>
          <a
            href={productLink}
            target="_blank"
            rel="noreferrer"
            style={{
              cursor: 'pointer',
              color: theme.colors.primary,
              marginTop: '12px',
              display: 'block'
            }}
          >
            {name}
          </a>
          {tooltipId && <div id={tooltipId} style={{ display: 'none' }}></div>}
        </div>
      );

      return ReactDOMServer.renderToStaticMarkup(linkElement);
    }

    return name;
  };

  return (
    <BeaconChartWithLegend
      title={title}
      loading={isLoading}
      includeSummaryInfo={false}
      subtitle={getSubtitle()}
      primaryLegendProps={{
        displayName: mainTimePeriodDisplayName
      }}
      convertSeriesToDelimitedData={async () => {
        const dataForExport = chartSeries[0]?.data || [];
        saveSingleBarChartAsDelimitedData(dataForExport, {
          fileTitle: `VariationsPageSize-${entityType}`
        });
      }}
      saveImageRef={saveImageRef}
      loadingComponent={<SplineChartLoading width={900} />}
    >
      <TopEntityChartV2
        chartSeries={chartSeries}
        seriesColors={[theme.colors.primary]}
        xAxisFormatter={xAxisFormatter}
        chartOptionsOverride={{
          chart: {
            height: 400
          },
          plotOptions: {
            series: {
              cropThreshold: 0
            }
          }
        }}
      />
    </BeaconChartWithLegend>
  );
};
